//src/components/admin/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import { 
  Users, CreditCard, Terminal, Activity, BarChart as ChartBar, FileText, 
  Bell, Settings, Building2, Zap, TrendingUp, DollarSign, Clock, 
  AlertCircle, CheckCircle, RefreshCw, Eye, Shield, Database, ArrowUp, ArrowDown
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { CHART } from '../../lib/chart-tokens';

interface DashboardStats {
  totalUsers: number;
  adminUsers: number;
  businessUsers: number;
  agencyUsers: number;
  paidUsers: number;
  freeUsers: number;
  enterpriseUsers: number;
  totalProposals: number;
  basicProposals: number;
  enhancedProposals: number;
  recentActivity: any[];
  userGrowthData: any[];
  subscriptionData: any[];
  proposalTypeData: any[];
  systemHealth: {
    database: boolean;
    rpcFunctions: boolean;
    userAuth: boolean;
  };
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    adminUsers: 0,
    businessUsers: 0,
    agencyUsers: 0,
    paidUsers: 0,
    freeUsers: 0,
    enterpriseUsers: 0,
    totalProposals: 0,
    basicProposals: 0,
    enhancedProposals: 0,
    recentActivity: [],
    userGrowthData: [],
    subscriptionData: [],
    proposalTypeData: [],
    systemHealth: {
      database: false,
      rpcFunctions: false,
      userAuth: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load all data in parallel for faster performance
      const [health, userStats, proposalStats, activity] = await Promise.all([
        checkSystemHealth(),
        loadUserStats(),
        loadProposalStats(),
        loadRecentActivity()
      ]);

      // Generate growth data after parallel loads complete (real, query-backed)
      const growthData = await generateGrowthData(proposalStats);

      setStats({
        ...userStats,
        ...proposalStats,
        recentActivity: activity,
        userGrowthData: growthData.userGrowth,
        subscriptionData: growthData.subscriptions,
        proposalTypeData: growthData.proposalTypes,
        systemHealth: health
      });

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSystemHealth = async () => {
    const health = {
      database: false,
      rpcFunctions: false,
      userAuth: false
    };

    try {
      // Run all health checks in parallel
      const [dbResult, rpcResult, authResult] = await Promise.all([
        supabase.from('profiles').select('id').limit(1),
        supabase.rpc('admin_list_users', { p_limit: 1, p_offset: 0 }),
        supabase.auth.getUser()
      ]);

      health.database = !!dbResult.data;
      health.rpcFunctions = !rpcResult.error;
      health.userAuth = !!authResult.data?.user;
    } catch (error) {
      console.error('Health check failed:', error);
    }

    return health;
  };

  const loadUserStats = async () => {
    try {
      const { data: users, error } = await supabase
        .from('profiles')
        .select('role, membership_tier, subscription_status, created_at');

      if (error) throw error;

      const totalUsers = users?.length || 0;
      const adminUsers = users?.filter(u => u.role === 'admin').length || 0;
      const businessUsers = users?.filter(u => u.role === 'business').length || 0;
      const agencyUsers = users?.filter(u => u.role === 'agency').length || 0;
      const paidUsers = users?.filter(u => u.membership_tier !== 'free').length || 0;
      const freeUsers = users?.filter(u => u.membership_tier === 'free').length || 0;
      const enterpriseUsers = users?.filter(u => u.membership_tier === 'enterprise').length || 0;

      return {
        totalUsers,
        adminUsers,
        businessUsers,
        agencyUsers,
        paidUsers,
        freeUsers,
        enterpriseUsers
      };
    } catch (error) {
      console.error('Error loading user stats:', error);
      return {
        totalUsers: 0,
        adminUsers: 0,
        businessUsers: 0,
        agencyUsers: 0,
        paidUsers: 0,
        freeUsers: 0,
        enterpriseUsers: 0
      };
    }
  };

  const loadProposalStats = async () => {
    try {
      // Get proposal statistics from proposal_templates table
      const { data: proposals, error } = await supabase
        .from('proposal_templates')
        .select('proposal_type, created_at');

      if (error) {
        console.warn('Could not load proposal stats:', error.message);
        return {
          totalProposals: 0,
          basicProposals: 0,
          enhancedProposals: 0
        };
      }

      const totalProposals = proposals?.length || 0;
      const basicProposals = proposals?.filter(p => p.proposal_type === 'basic').length || 0;
      const enhancedProposals = proposals?.filter(p => p.proposal_type === 'enhanced').length || 0;

      return {
        totalProposals,
        basicProposals,
        enhancedProposals
      };
    } catch (error) {
      console.error('Error loading proposal stats:', error);
      return {
        totalProposals: 0,
        basicProposals: 0,
        enhancedProposals: 0
      };
    }
  };

  const loadRecentActivity = async () => {
    try {
      // Run both queries in parallel
      const [usersResult, proposalsResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('email, company_name, role, created_at')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('proposal_templates')
          .select('proposal_type, created_at, profiles!inner(company_name, email)')
          .order('created_at', { ascending: false })
          .limit(5)
      ]);

      const activity: any[] = [];

      if (usersResult.data) {
        activity.push(...usersResult.data.map(user => ({
          type: 'user_registration',
          description: `New ${user.role} user: ${user.company_name || user.email || 'Unknown'}`,
          timestamp: user.created_at,
          icon: Users
        })));
      }

      if (proposalsResult.data) {
        activity.push(...proposalsResult.data.map(proposal => ({
          type: 'proposal_created',
          description: `New ${proposal.proposal_type} proposal by ${proposal.profiles?.company_name || proposal.profiles?.email || 'Unknown'}`,
          timestamp: proposal.created_at,
          icon: FileText
        })));
      }

      // Sort all activity by timestamp
      return activity
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 10);

    } catch (error) {
      console.error('Error loading recent activity:', error);
      return [];
    }
  };

  // Build REAL chart data from the database — a 7-day cumulative trend from
  // created_at timestamps and a real subscription-tier distribution. (Previously
  // this fabricated the trend with Math.random and estimated the tier split.)
  const generateGrowthData = async (proposalStats: any) => {
    const [{ data: profileRows }, { data: proposalRows }] = await Promise.all([
      supabase.from('profiles').select('created_at, membership_tier'),
      supabase.from('proposal_templates').select('created_at'),
    ]);
    const profiles = profileRows || [];
    const proposals = proposalRows || [];

    // 7-day cumulative trend (total users / proposals existing as of each day).
    const userGrowth = [] as Array<{ date: string; users: number; proposals: number }>;
    for (let i = 6; i >= 0; i--) {
      const day = new Date();
      day.setHours(23, 59, 59, 999);
      day.setDate(day.getDate() - i);
      const cutoff = day.getTime();
      userGrowth.push({
        date: day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        users: profiles.filter(p => p.created_at && new Date(p.created_at).getTime() <= cutoff).length,
        proposals: proposals.filter(p => p.created_at && new Date(p.created_at).getTime() <= cutoff).length,
      });
    }

    // Real subscription-tier distribution from membership_tier.
    const tierCount = (t: string) => profiles.filter(p => (p.membership_tier || 'free').toLowerCase() === t).length;
    const knownTiers = ['free', 'basic', 'pro', 'enterprise'];
    const otherCount = profiles.filter(p => !knownTiers.includes((p.membership_tier || 'free').toLowerCase())).length;
    const subscriptions = [
      { name: 'Free', value: tierCount('free'), color: CHART.neutral },
      { name: 'Basic', value: tierCount('basic'), color: CHART.primary },
      { name: 'Pro', value: tierCount('pro'), color: CHART.strong },
      { name: 'Enterprise', value: tierCount('enterprise'), color: CHART.moderate },
      ...(otherCount > 0 ? [{ name: 'Other', value: otherCount, color: CHART.moderate }] : []),
    ].filter(s => s.value > 0);

    // Proposal type distribution (already real from proposalStats).
    const proposalTypes = [
      { name: 'Basic', value: proposalStats.basicProposals, color: CHART.neutral },
      { name: 'Enhanced', value: proposalStats.enhancedProposals, color: CHART.primary },
    ];

    return { userGrowth, subscriptions, proposalTypes };
  };

  const healthIcon = (status: boolean) => status ? 
    <CheckCircle className="h-4 w-4 text-success" /> : 
    <AlertCircle className="h-4 w-4 text-urgent" />;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servet-blue"></div>
          <p className="text-bw-muted mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-bw-ink">Admin Dashboard</h1>
          <p className="text-bw-muted">Platform overview and system management</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-bw-muted">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </div>
          <button
            onClick={loadDashboardData}
            className="flex items-center px-3 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary/90"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* System Health Alert */}
      <div className="bg-bw-surface p-4 rounded-lg shadow-card border border-bw-border">
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          <Shield className="h-5 w-5 mr-2 text-bw-primary" />
          System Health
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-3 bg-bw-surface-subtle rounded">
            <span className="text-sm font-medium">Database</span>
            <div className="flex items-center">
              {healthIcon(stats.systemHealth.database)}
              <span className="ml-2 text-sm">{stats.systemHealth.database ? 'Connected' : 'Error'}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-bw-surface-subtle rounded">
            <span className="text-sm font-medium">RPC Functions</span>
            <div className="flex items-center">
              {healthIcon(stats.systemHealth.rpcFunctions)}
              <span className="ml-2 text-sm">{stats.systemHealth.rpcFunctions ? 'Working' : 'Error'}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-bw-surface-subtle rounded">
            <span className="text-sm font-medium">Authentication</span>
            <div className="flex items-center">
              {healthIcon(stats.systemHealth.userAuth)}
              <span className="ml-2 text-sm">{stats.systemHealth.userAuth ? 'Active' : 'Error'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Total Users</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.totalUsers}</p>
              <p className="text-xs text-bw-muted mt-1">
                {stats.adminUsers} admin, {stats.businessUsers} business, {stats.agencyUsers} agency
              </p>
            </div>
            <Users className="h-8 w-8 text-bw-primary" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Revenue Users</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.paidUsers}</p>
              <p className="text-xs text-bw-muted mt-1">
                {stats.enterpriseUsers} enterprise tier
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-success" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Total Proposals</p>
              <p className="text-2xl font-bold text-bw-ink">{stats.totalProposals}</p>
              <p className="text-xs text-bw-muted mt-1">
                {stats.enhancedProposals} enhanced, {stats.basicProposals} basic
              </p>
            </div>
            <FileText className="h-8 w-8 text-bw-primary" />
          </div>
        </div>

        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-bw-muted">Conversion Rate</p>
              <p className="text-2xl font-bold text-bw-ink">
                {stats.totalUsers > 0 ? Math.round((stats.paidUsers / stats.totalUsers) * 100) : 0}%
              </p>
              <p className="text-xs text-bw-muted mt-1">Free to paid conversion</p>
            </div>
            <TrendingUp className="h-8 w-8 text-bw-primary" />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Growth Chart */}
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <h3 className="text-lg font-semibold mb-4">Growth Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats.userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="users" stroke={CHART.primary} strokeWidth={2} name="Users" />
              <Line type="monotone" dataKey="proposals" stroke={CHART.strong} strokeWidth={2} name="Proposals" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Subscription Distribution */}
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <h3 className="text-lg font-semibold mb-4">User Tiers</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={stats.subscriptionData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {stats.subscriptionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {stats.subscriptionData.map((item, index) => (
              <div key={index} className="flex items-center">
                <div 
                  className="w-2 h-2 rounded-full mr-2" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-xs">{item.name}: {item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Proposal Types */}
        <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
          <h3 className="text-lg font-semibold mb-4">Proposal Types</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.proposalTypeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill={CHART.primary} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 text-center">
            <p className="text-sm text-bw-muted">
              Enhanced proposals: {stats.totalProposals > 0 ? Math.round((stats.enhancedProposals / stats.totalProposals) * 100) : 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-bw-surface rounded-lg shadow-card border border-bw-border">
        <div className="p-6 border-b border-bw-border">
          <h3 className="text-lg font-semibold">Recent Activity</h3>
        </div>
        <div className="p-6">
          {stats.recentActivity.length > 0 ? (
            <div className="space-y-4">
              {stats.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <activity.icon className="h-5 w-5 text-bw-muted" />
                  <div className="flex-1">
                    <p className="text-sm text-bw-ink">{activity.description}</p>
                    <p className="text-xs text-bw-muted">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-bw-muted text-center py-8">No recent activity</p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-bw-surface p-6 rounded-lg shadow-card border border-bw-border">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => window.location.href = '/admin/users'}
            className="flex items-center justify-center space-x-2 p-3 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
          >
            <Users className="h-4 w-4" />
            <span>Manage Users</span>
          </button>
          <button
            onClick={() => window.location.href = '/admin/system'}
            className="flex items-center justify-center space-x-2 p-3 bg-success text-white rounded-lg hover:bg-success transition-colors"
          >
            <Terminal className="h-4 w-4" />
            <span>System Maintenance</span>
          </button>
          <button
            onClick={() => window.location.href = '/admin/blog'}
            className="flex items-center justify-center space-x-2 p-3 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
          >
            <FileText className="h-4 w-4" />
            <span>Manage Content</span>
          </button>
          <button
            onClick={loadDashboardData}
            className="flex items-center justify-center space-x-2 p-3 bg-neutral-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>
    </div>
  );
}